import React, { useMemo, useState, createRef } from "react";
import "./ExperienceCard.scss";
import ColorThief from "colorthief";

/**
 * ExperienceCard supports:
 * - bg: one field that controls BOTH banner + card body.
 *   - If bg is a gradient (contains "gradient("), it's used directly for banner.
 *   - Otherwise bg is treated as a color and we auto-generate a gradient for banner + a tinted body bg.
 * - Optional overrides:
 *   - bannerColor / color: override banner only
 *   - cardBg / cardBgDark: override body only (dark mode optional)
 */

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

function clampInt(n, min, max, fallback) {
  const x = Number.parseInt(n, 10);
  if (!Number.isFinite(x)) return fallback;
  return Math.min(max, Math.max(min, x));
}

function isGradient(str) {
  if (!str) return false;
  const s = String(str).toLowerCase();
  return s.includes("gradient(");
}

function isHexColor(str) {
  if (!str) return false;
  const s = String(str).trim();
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(s);
}

function hexToRgb(hex) {
  const h = String(hex).replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const hasAlpha = full.length === 8;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return null;

  const r = (n >> (hasAlpha ? 24 : 16)) & 255;
  const g = (n >> (hasAlpha ? 16 : 8)) & 255;
  const b = (n >> (hasAlpha ? 8 : 0)) & 255;
  const a = hasAlpha ? (n & 255) / 255 : 1;

  return { r, g, b, a };
}

function rgbToCss({ r, g, b, a = 1 }) {
  const aa = clamp01(a);
  if (aa >= 0.999) return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${aa})`;
}

function mixRgb(c1, c2, t) {
  const tt = clamp01(t);
  return {
    r: c1.r + (c2.r - c1.r) * tt,
    g: c1.g + (c2.g - c1.g) * tt,
    b: c1.b + (c2.b - c1.b) * tt,
    a: (c1.a ?? 1) + ((c2.a ?? 1) - (c1.a ?? 1)) * tt,
  };
}

function darken(rgb, amount = 0.25) {
  const black = { r: 0, g: 0, b: 0, a: 1 };
  return mixRgb(rgb, black, clamp01(amount));
}

function lighten(rgb, amount = 0.15) {
  const white = { r: 255, g: 255, b: 255, a: 1 };
  return mixRgb(rgb, white, clamp01(amount));
}

function rgb(values) {
  return typeof values === "undefined" ? null : "rgb(" + values.join(", ") + ")";
}

// If bg is a hex, we can build a nice gradient and tinted body background.
// If bg is not hex (e.g. "rgb(...)" or "hsl(...)"), we just use it directly (no math).
function deriveFromBg(bg, isDark) {
  const s = String(bg);

  // Gradient provided by user -> use directly for banner, and body falls back to defaults unless overridden.
  if (isGradient(s)) {
    return {
      bannerBg: s,
      cardBg: s,
      overlayOpacity: 0.45,
    };
  }

  // Hex -> auto style
  if (isHexColor(s)) {
    const base = hexToRgb(s);
    if (!base) return { bannerBg: s, cardBg: null, overlayOpacity: 0.45 };

    // Banner gradient: slightly lighter at top, darker at bottom (looks premium)
    const top = lighten(base, 0.18);
    const bottom = darken(base, 0.38);
    const bannerBg = `linear-gradient(180deg, ${rgbToCss(top)} 0%, ${rgbToCss(bottom)} 100%)`;

    // Body background: subtle tint, darker in dark mode
    const bodyMixTarget = isDark ? { r: 0, g: 0, b: 0, a: 1 } : { r: 255, g: 255, b: 255, a: 1 };
    const body = mixRgb(base, bodyMixTarget, isDark ? 0.78 : 0.92);
    const cardBg = rgbToCss({ ...body, a: 1 });

    return {
      bannerBg,
      cardBg,
      overlayOpacity: 0.55,
    };
  }

  // Non-hex color string -> use directly for banner; body will not be auto-derived.
  return {
    bannerBg: s,
    cardBg: null,
    overlayOpacity: 0.45,
  };
}

export default function ExperienceCard({ cardInfo, isDark }) {
  const [colorArrays, setColorArrays] = useState([]);
  const imgRef = createRef();

  function getColorArrays() {
    try {
      const colorThief = new ColorThief();
      const c = colorThief.getColor(imgRef.current);
      if (Array.isArray(c) && c.length === 3) setColorArrays(c);
    } catch (e) {
      // ignore – fall back to defaults
    }
  }

  // One-field mode: bg drives both banner + card (preferred)
// Also supports CMS gradient controls if bg isn't precomputed yet.
  const manualBg = useMemo(() => {
    const c = cardInfo?.bg;
    if (c) return String(c);

    const from = cardInfo?.bgFrom ? String(cardInfo.bgFrom) : "";
    const mid = cardInfo?.bgTo ? String(cardInfo.bgTo) : "";
    const to = cardInfo?.bgTo2 ? String(cardInfo.bgTo2) : "";

    if (from && (mid || to)) {
      const deg = clampInt(cardInfo?.bgDeg, 0, 360, 180);
      const fromPct = clampInt(cardInfo?.bgFromPct, 0, 100, 0);
      const midPct = clampInt(cardInfo?.bgToPct, 0, 100, 55);
      const toPct = clampInt(cardInfo?.bgTo2Pct, 0, 100, 100);

      if (from && mid && to) {
        return `linear-gradient(${deg}deg, ${from} ${fromPct}%, ${mid} ${midPct}%, ${to} ${toPct}%)`;
      }

      const end = to || mid;
      return `linear-gradient(${deg}deg, ${from} ${fromPct}%, ${end} ${toPct}%)`;
    }

    return null;
  }, [cardInfo]);

  // Optional per-part overrides (still supported)
  const manualBannerColor = useMemo(() => {
    const c = cardInfo?.bannerColor || cardInfo?.color;
    return c ? String(c) : null;
  }, [cardInfo]);

  const manualCardBg = useMemo(() => {
    const c = cardInfo?.cardBg || cardInfo?.cardBackground || cardInfo?.bgColor;
    return c ? String(c) : null;
  }, [cardInfo]);

  const manualCardBgDark = useMemo(() => {
    const c = cardInfo?.cardBgDark || cardInfo?.cardBackgroundDark;
    return c ? String(c) : null;
  }, [cardInfo]);

  // Extracted banner bg when nothing is provided
  const extractedBannerBg = useMemo(() => {
    const extracted = rgb(colorArrays);
    return extracted || null;
  }, [colorArrays]);

  // Decide banner + card bg based on priority:
  // 1) bannerColor/cardBg overrides
  // 2) bg single-field derived
  // 3) color thief (banner only)
  // 4) SCSS defaults
  const derived = useMemo(() => {
    if (!manualBg) return null;
    return deriveFromBg(manualBg, isDark);
  }, [manualBg, isDark]);

  const effectiveBannerBg = useMemo(() => {
    if (manualBannerColor) return manualBannerColor;
    if (derived?.bannerBg) return derived.bannerBg;
    if (extractedBannerBg) return extractedBannerBg;
    return null;
  }, [manualBannerColor, derived, extractedBannerBg]);

  const effectiveCardBg = useMemo(() => {
    if (isDark && manualCardBgDark) return manualCardBgDark;
    if (manualCardBg) return manualCardBg;
    if (derived?.cardBg) return derived.cardBg;
    return null;
  }, [isDark, manualCardBg, manualCardBgDark, derived]);

  const overlayOpacity = useMemo(() => {
    // If user gave bannerColor, make overlay strong to avoid muddy mixes.
    if (manualBannerColor) return 0.9;
    if (derived?.overlayOpacity != null) return derived.overlayOpacity;
    return 0.45;
  }, [manualBannerColor, derived]);

  // Set CSS variables on the card root so both banner + body can read them.
  const cssVars = useMemo(() => {
    const vars = {};

    if (effectiveCardBg) vars["--experience-card-bg"] = effectiveCardBg;
    if (effectiveBannerBg) vars["--experience-banner-bg"] = effectiveBannerBg;
    vars["--experience-banner-overlay-opacity"] = overlayOpacity;

    return Object.keys(vars).length ? vars : undefined;
  }, [effectiveCardBg, effectiveBannerBg, overlayOpacity]);

  const GetDescBullets = ({ descBullets, isDark }) => {
    return descBullets
      ? descBullets.map((item, i) => (
          <li key={i} className={isDark ? "subTitle dark-mode-text" : "subTitle"}>
            {item}
          </li>
        ))
      : null;
  };

  return (
    <div style={cssVars} className={isDark ? "experience-card-dark" : "experience-card"}>
      <div className="experience-banner">
        <div className="experience-blurred_div" />

        <div className="experience-div-company">
          <h5 className="experience-text-company">{cardInfo.company}</h5>
        </div>

        <img
          crossOrigin={"anonymous"}
          ref={imgRef}
          className="experience-roundedimg"
          src={cardInfo.companylogo}
          alt={cardInfo.company}
          onLoad={() => getColorArrays()}
        />
      </div>

      <div className="experience-text-details">
        <h5 className={isDark ? "experience-text-role dark-mode-text" : "experience-text-role"}>
          {cardInfo.role}
        </h5>

        <h5 className={isDark ? "experience-text-date dark-mode-text" : "experience-text-date"}>
          {cardInfo.date}
        </h5>

        <p className={isDark ? "subTitle experience-text-desc dark-mode-text" : "subTitle experience-text-desc"}>
          {cardInfo.desc}
        </p>

        <ul>
          <GetDescBullets descBullets={cardInfo.descBullets} isDark={isDark} />
        </ul>
      </div>
    </div>
  );
}
